import api, { setAccessToken, clearTokens, getErrorMessage } from './api';
import { mockAuthService } from './mockData';

// Use mock data for demo (set to false when backend is available)
const USE_MOCK_DATA = true;

// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  username: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  username: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
}

// Auth service functions
export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<User> {
    if (USE_MOCK_DATA) {
      return mockAuthService.login(credentials);
    }
    
    try {
      // Auth service expects form data for login
      const formData = new URLSearchParams();
      formData.append('username', credentials.email); // OAuth2 uses 'username' field
      formData.append('password', credentials.password);

      const response = await api.post<AuthResponse>('/api/v1/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      // Store the token
      setAccessToken(response.data.access_token);

      // Get user profile
      return await this.getCurrentUser();
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Register a new user
   */
  async register(data: RegisterData): Promise<User> {
    if (USE_MOCK_DATA) {
      return mockAuthService.register(data);
    }
    
    try {
      const response = await api.post<User>('/api/v1/auth/register', data);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    if (USE_MOCK_DATA) {
      return mockAuthService.getCurrentUser();
    }
    
    try {
      const response = await api.get<User>('/api/v1/auth/me');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Logout - clear tokens
   */
  logout(): void {
    if (USE_MOCK_DATA) {
      mockAuthService.logout();
    } else {
      clearTokens();
    }
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<void> {
    try {
      await api.post('/api/v1/auth/verify-email', { token });
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Deactivate account
   */
  async deactivateAccount(): Promise<void> {
    try {
      await api.post('/api/v1/auth/deactivate');
      clearTokens();
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};
